import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { kpiApi, type AnnualPerformance, type OtkupPerformance } from "../../api/kpi";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Select } from "../../components/Select";
import { formatNumber } from "../../lib/format";

/**
 * KPI overview + a Set-targets modal per user.
 *
 * <p>The table is read-only at a glance: each row is one sales rep (or the
 * director), columns show monthly targets and invoiced orders side by side
 * so attainment is visible without drilling. Editing goes through a focused
 * modal — admin enters a yearly target, the system spreads it evenly across
 * 12 months, and the admin can fine-tune individual months before saving.
 * Realisation counts INVOICED orders only — anything earlier in the pipeline
 * (approved, in production, shipped, delivered) hasn't been billed and so
 * doesn't count toward the target.
 */
export default function AdminKpiPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState<AnnualPerformance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AnnualPerformance | null>(null);
  // Procurement is a separate list, not a separate column: one table in euro
  // and one in tonnes beats a table where the reader has to remember which is which.
  const [otkup, setOtkup] = useState<OtkupPerformance[] | null>(null);
  // Procurement counts by crop year, 1 September to 31 August, so it has a picker of its own.
  // Sales keeps the calendar year above.
  const tekuciRod = currentCropYear(now);
  const [rod, setRod] = useState(tekuciRod);

  async function load() {
    setError(null);
    try {
      setRows(await kpiApi.team(year));
    } catch (e) {
      setError((e as ApiError).message);
      setRows([]);
    }
  }
  useEffect(() => { void load(); }, [year]);

  useEffect(() => {
    void (async () => {
      try {
        setOtkup(await kpiApi.otkupTeam(rod));
      } catch {
        // A portal with no buy-in desk yet is not an error on this screen.
        setOtkup([]);
      }
    })();
  }, [rod]);

  const canSetDirector = hasPermission("kpi:set_director");
  const canSetRep = hasPermission("kpi:set_rep");

  function canEdit(role: string) {
    return role === "SALES_DIRECTOR" ? canSetDirector : canSetRep;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.kpi.title", "KPI targets")}</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
            {t("admin.kpi.subtitle",
              "Monthly revenue targets per sales rep. Realisation counts INVOICED orders only; € actuals land with the ERP integration.")}
          </p>
        </div>
        <label style={{ fontSize: 13, color: "var(--ink-2)", display: "flex", alignItems: "center", gap: 8 }}>
          {t("admin.kpi.year", "Year")}
          <Select<string>
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            ariaLabel={t("admin.kpi.year", "Year")}
            style={{ width: "100%" }}
            options={[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]
              .map((y) => ({ value: String(y), label: String(y) }))}
          />
        </label>
      </header>

      {error && <div style={errorBox}>{error}</div>}

      {otkup && otkup.length > 0 && (
        <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: 16, color: "var(--brand-ink)" }}>
              {t("admin.kpi.otkupTitle", "Procurement — tonnes per season")}
            </h2>
            <label style={{ fontSize: 13, color: "var(--ink-2)", display: "flex", alignItems: "center", gap: 8 }}>
              {t("admin.kpi.otkupCropYearLabel", "Crop year")}
              <Select<string>
                value={String(rod)}
                onChange={(v) => setRod(Number(v))}
                ariaLabel={t("admin.kpi.otkupCropYearLabel", "Crop year")}
                style={{ width: "100%" }}
                options={[tekuciRod - 1, tekuciRod, tekuciRod + 1]
                  .map((y) => ({ value: String(y), label: String(y) }))}
              />
            </label>
          </div>
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("admin.kpi.otkupSubtitle", "Only settled deliveries count. An announced load is a promise, not performance.")}
          </p>
          {/* The procurement year is the crop year, harvest to harvest, and not the calendar year of sales. */}
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
            {t("admin.kpi.otkupCropYear", "Crop year {{year}}: from 1 September {{year}} to 31 August {{next}}.", {
              year: rod,
              next: rod + 1,
            })}
          </p>
          <table className="tbl">
            <thead>
              <tr>
                <th style={otkupTh}>{t("admin.kpi.otkupUser", "Buy-in desk")}</th>
                <th style={otkupTh}>{t("admin.kpi.otkupSuppliers", "Co-operatives")}</th>
                <th style={otkupTh}>{t("admin.kpi.otkupTarget", "Target (t)")}</th>
                <th style={otkupTh}>{t("admin.kpi.otkupActual", "Settled (t)")}</th>
                <th style={otkupTh}>{t("admin.kpi.otkupAttainment", "Attainment")}</th>
              </tr>
            </thead>
            <tbody>
              {otkup.map((o) => (
                <tr key={o.userId}>
                  <td style={otkupTd}>{o.userName}</td>
                  <td style={otkupTd}>{o.supplierCount}</td>
                  <td style={otkupTd}>{o.target}</td>
                  <td style={otkupTd}>{o.actual}</td>
                  <td style={otkupTd}>
                    {o.attainment === null ? "—" : `${Math.round(o.attainment * 100)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {!rows ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.kpi.empty", "No sales staff yet — assign SALES or SALES_DIRECTOR roles first.")}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map((r) => (
            <UserCard
              key={r.userId}
              row={r}
              editable={canEdit(r.role)}
              onEdit={() => setEditing(r)}
            />
          ))}
        </div>
      )}

      {editing && (
        <SetTargetsModal
          row={editing}
          year={year}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

function UserCard({ row, editable, onEdit }: { row: AnnualPerformance; editable: boolean; onEdit: () => void }) {
  const { t } = useTranslation();
  const target = Number(row.totalTargetEur);
  const monthlyMax = Math.max(1, ...row.months.map((m) => Number(m.targetRevenueEur ?? 0)));
  return (
    <div style={cardOuter}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <strong style={{ fontSize: 15, color: "var(--brand-ink)" }}>{row.userName}</strong>
          <div style={{ fontSize: 11, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>
            {row.role === "SALES_DIRECTOR" ? t("admin.kpi.role.director", "Sales director") : t("admin.kpi.role.sales", "Sales")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <Stat label={t("admin.kpi.col.target", "Annual target")} value={`€ ${formatEur(target)}`} accent="var(--brand-ink)" />
          <Stat label={t("admin.kpi.col.invoiced", "Invoiced YTD")} value={String(row.totalInvoicedOrders)} accent="var(--ok)" />
          <button
            type="button"
            disabled={!editable}
            onClick={onEdit}
            style={editable ? primaryBtn : disabledBtn}
            title={editable ? "" : t("admin.kpi.notAllowed", "You don't have permission to edit this user.")}
          >
            {target > 0 ? t("admin.kpi.editTargets", "Edit targets") : t("admin.kpi.setTargets", "Set targets")}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 6 }}>
        {row.months.map((m) => {
          const v = Number(m.targetRevenueEur ?? 0);
          const h = v <= 0 ? 4 : Math.max(4, Math.round((v / monthlyMax) * 36));
          return (
            <div key={m.month} title={`${MONTHS[m.month - 1]} — target € ${v ? formatEur(v) : "—"}, ${m.invoicedOrderCount ?? "—"} invoiced`}
                 style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: "100%", height: 40, display: "flex", alignItems: "flex-end" }}>
                <div style={{ width: "100%", height: h, background: v ? "var(--brand-primary)" : "var(--surface-muted)", borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 10, color: "var(--ink-4)" }}>{MONTHS_SHORT[m.month - 1]}</span>
              {m.invoicedOrderCount != null && m.invoicedOrderCount > 0 && (
                <span style={{ fontSize: 10, color: "var(--ok)", fontWeight: 600 }}>{m.invoicedOrderCount}</span>
              )}
              {/* A month after the day the invoices reach has no figure yet: a dash, not a zero. */}
              {m.invoicedOrderCount == null && (
                <span style={{ fontSize: 10, color: "var(--ink-4)" }}>—</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SetTargetsModal({
  row, year, onClose, onSaved,
}: {
  row: AnnualPerformance;
  year: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const initialMonthly = useMemo(
    () => row.months.map((m) => Number(m.targetRevenueEur ?? 0)),
    [row],
  );
  const [monthly, setMonthly] = useState<number[]>(initialMonthly);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const total = monthly.reduce((s, n) => s + (Number.isFinite(n) ? n : 0), 0);

  // Even split rounded to nearest €100, with any remainder loaded onto December.
  function splitAnnual(annual: number) {
    if (!Number.isFinite(annual) || annual <= 0) {
      setMonthly(new Array(12).fill(0));
      return;
    }
    const base = Math.round((annual / 12) / 100) * 100;
    const next = new Array(12).fill(base);
    const drift = annual - base * 12;
    if (drift !== 0) next[11] = Math.max(0, next[11] + drift);
    setMonthly(next);
  }

  function setOne(idx: number, raw: string) {
    const n = Number(raw.replace(/[^0-9.]/g, ""));
    setMonthly((cur) => cur.map((v, i) => (i === idx ? (Number.isFinite(n) && n >= 0 ? n : 0) : v)));
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      // Only push months that changed — keeps audit log clean.
      const writes = monthly
        .map((v, i) => ({ month: i + 1, v, before: initialMonthly[i] }))
        .filter((m) => m.v !== m.before);
      for (const w of writes) {
        await kpiApi.setTarget(row.userId, year, w.month, { targetRevenueEur: w.v });
      }
      onSaved();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setSaving(false);
    }
  }

  async function copyPreviousYear(percentDelta: number) {
    setErr(null);
    try {
      const prev = await kpiApi.userPerformance(row.userId, year - 1);
      const next = prev.months.map((m) => {
        const v = Number(m.targetRevenueEur ?? 0);
        return Math.round(v * (1 + percentDelta / 100));
      });
      // If previous year was entirely empty, leave the form alone.
      if (next.every((n) => n === 0)) {
        setErr(t("admin.kpi.modal.prevEmpty", "Previous year has no targets to copy."));
        return;
      }
      setMonthly(next);
    } catch (e) {
      setErr((e as ApiError).message);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{row.userName}</h2>
            <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
              {t("admin.kpi.modal.subtitle", "Set monthly targets for {{year}}", { year })}
            </p>
          </div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Close">×</button>
        </header>

        <section style={section}>
          <label style={fieldLabel}>{t("admin.kpi.modal.annual", "Annual target (€)")}</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              inputMode="numeric"
              value={total > 0 ? String(total) : ""}
              placeholder="e.g. 1200000"
              onChange={(e) => splitAnnual(Number(e.target.value.replace(/[^0-9.]/g, "")))}
              style={{ ...input, flex: "1 1 200px", fontSize: 15, fontWeight: 600 }}
            />
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
              {t("admin.kpi.modal.autoSplit", "Auto-splits evenly across 12 months")}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={() => copyPreviousYear(0)} style={ghostBtn}>
              {t("admin.kpi.modal.copyPrev", "Copy {{year}}", { year: year - 1 })}
            </button>
            <button type="button" onClick={() => copyPreviousYear(10)} style={ghostBtn}>
              {t("admin.kpi.modal.copyPrevPlus", "Copy {{year}} +10%", { year: year - 1 })}
            </button>
            <button type="button" onClick={() => setMonthly(new Array(12).fill(0))} style={ghostBtn}>
              {t("admin.kpi.modal.clear", "Clear all")}
            </button>
          </div>
        </section>

        <section style={section}>
          <label style={fieldLabel}>{t("admin.kpi.modal.fineTune", "Fine-tune by month")}</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
            {monthly.map((v, idx) => (
              <div key={idx} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {MONTHS[idx]}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={v > 0 ? String(v) : ""}
                  placeholder="—"
                  onChange={(e) => setOne(idx, e.target.value)}
                  style={{ ...input, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                />
              </div>
            ))}
          </div>
        </section>

        {err && <div style={errorBox}>{err}</div>}

        <footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
          <span style={{ fontSize: 13, color: "var(--ink-2)" }}>
            <strong>{t("admin.kpi.modal.totalLabel", "Total")}: </strong>
            <span style={{ fontWeight: 700, color: "var(--brand-ink)" }}>€ {formatEur(total)}</span>
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose} style={ghostBtn} disabled={saving}>
              {t("common.cancel", "Cancel")}
            </button>
            <button type="button" onClick={save} style={primaryBtn} disabled={saving}>
              {saving ? t("common.saving", "Saving…") : t("admin.kpi.modal.save", "Save targets")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent, marginTop: 2 }}>{value}</div>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_SHORT = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

/**
 * The crop year running on a given day. It starts on 1 September, so from January to August the
 * running one is the crop year that began the autumn before, and the calendar year names one not yet begun.
 */
function currentCropYear(d: Date): number {
  return d.getMonth() < 8 ? d.getFullYear() - 1 : d.getFullYear();
}

function formatEur(n: number): string {
  if (!Number.isFinite(n) || n === 0) return "0";
  return formatNumber(n, undefined, { maximumFractionDigits: 0 });
}

const input: React.CSSProperties = { padding: "8px 10px", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, background: "var(--surface)", width: "100%", boxSizing: "border-box" };
const fieldLabel: React.CSSProperties = { fontSize: 12, color: "var(--ink-2)", fontWeight: 600, marginBottom: 6, display: "block", textTransform: "uppercase", letterSpacing: 0.4 };
const section: React.CSSProperties = { background: "var(--surface-2)", padding: 14, borderRadius: 8, border: "1px solid var(--border)" };
const cardOuter: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 18, boxShadow: "var(--shadow-1)" };
const primaryBtn: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const ghostBtn: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", color: "var(--ink-2)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer" };
const disabledBtn: React.CSSProperties = { ...primaryBtn, background: "var(--surface-muted)", color: "var(--ink-4)", border: "1px solid var(--border)", cursor: "not-allowed" };
const iconBtn: React.CSSProperties = { background: "transparent", border: "none", fontSize: 24, color: "var(--ink-4)", cursor: "pointer", lineHeight: 1, padding: 0, width: 28, height: 28 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "grid", placeItems: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { background: "var(--surface)", borderRadius: 12, padding: 24, width: "min(720px, 100%)", maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 18, boxShadow: "var(--shadow-2)" };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };

const otkupTh: React.CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "var(--ink-3)" };
const otkupTd: React.CSSProperties = { padding: "8px 10px", fontSize: 13 };
